<template>
  <div class="full-width q-pa-md">
    <div class="row items-center gutter-x-sm">
      <div class="col-auto gt-md">
        <q-icon name="timer" color="light" size="2rem"/>
      </div>

      <div class="col">
        <recent-time-entry-input
          v-model="timeEntry.description"
          @submit="handleSubmitActiveTimeEntry"
          @blur="handleActiveTimeEntryUpdated('description')"
          @select="handleSelectRecentTimeEntry"
        />
      </div>

      <div class="col-auto">
        <div>
          <customer-indicator v-if="hasCustomer" clickable :customer="customer"/>

          <q-btn
            v-else
            flat
            dense
            icon="person"
            color="faded"
          />

          <customer-popover
            :active-customer="customer"
            @select-customer="handleSelectCustomer"
          />
        </div>
      </div>

      <div class="col-auto">
        <div>
          <project-indicator v-if="hasProject" :project="project" clickable hide-customer/>

          <q-btn
            v-else
            flat
            dense
            icon="folder"
            color="faded"
          />

          <project-popover
            :active-project="project"
            @select-project="handleSelectProject"
          />
        </div>
      </div>

      <div class="col-auto">
        <q-checkbox
          keep-color
          v-model="timeEntry.billable"
          checked-icon="attach_money"
          unchecked-icon="money_off"
          :color="timeEntry.billable ? 'faded' : 'light'"
          @input="handleActiveTimeEntryUpdated('billable')"
        >
          <q-tooltip :delay="300">
            {{ $t('billable_tooltip') }}
          </q-tooltip>
        </q-checkbox>
      </div>

      <div v-if="isRunning" class="col-auto">
        <inline-edit-duration
          class="q-title text-faded"
          :duration="duration"
          :started-at="timeEntry.startedAt ? timeEntry.startedAt.date : null"
          :stopped-at="timeEntry.stoppedAt ? timeEntry.stoppedAt.date : null"
          @save="handleUpdateStartStoppedTime"
        />
      </div>

      <div class="auto">
        <q-btn
          v-if="isRunning"
          round
          color="red"
          icon="stop"
          :disabled="loading"
          :loading="loading"
          @click="handleStopActiveTimeEntry"
        />

        <q-btn
          v-else
          round
          color="green"
          icon="play_arrow"
          :disabled="loading"
          :loading="loading"
          @click="handleStartTimeEntry"
        />
      </div>

      <div class="col-auto">
        <q-btn
          flat
          dense
          round
          color="light"
          icon="delete"
          :disabled="!isRunning || loading"
          @click="handleDeleteActiveTimeEntry"
        />
      </div>
    </div>
  </div>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex'
  import CustomerIndicator from './CustomerIndicator.vue'
  import CustomerPopover from './CustomerPopover.vue'
  import InlineEditDuration from 'components/Common/Inputs/InlineEditDuration.vue'
  import ProjectIndicator from './ProjectIndicator.vue'
  import ProjectPopover from './ProjectPopover.vue'
  import RecentTimeEntryInput from './RecentTimeEntryInput.vue'
  import changeCase from 'change-case-object'
  import { createErrorFromResponse } from 'src/utils/error'
  import { currentUtcDateTime } from 'src/utils/date'
  import { date } from 'quasar'
  import debounceMixin from 'src/utils/mixins/debounce'
  import { projectTypes } from 'src/utils/projectTypes'

  function initialData () {
    return {
      description: null,
      billable: true,
      customerId: null,
      projectId: null,
      customer: null,
      project: null,
      startedAt: null,
      stoppedAt: null
    }
  }

  export default {
    data () {
      return {
        timeEntry: initialData(),
        now: new Date(),
        interval: null,
        loading: false
      }
    },

    computed: {
      ...mapGetters(['activeTimeEntry']),

      isRunning () {
        return !!this.activeTimeEntry
      },

      hasProject () {
        return !!this.project
      },

      hasCustomer () {
        return !!this.customer
      },

      activeTimeEntryDescription () {
        return this.activeTimeEntry ? this.activeTimeEntry.description : null
      },

      project () {
        return this.isRunning ? (this.activeTimeEntry.project_id ? this.activeTimeEntry.project.data : null) : this.timeEntry.project
      },

      customer () {
        return this.isRunning ? (this.activeTimeEntry.customer_id ? this.activeTimeEntry.customer.data : null) : this.timeEntry.customer
      },

      duration () {
        const startedAt = new Date(this.activeTimeEntry.started_at.date.replace(' ', 'T') + 'Z')

        return Math.max(0, date.getDateDiff(this.now, startedAt, 'seconds'))
      },

      mappedTimeEntry () {
        return {
          description: this.timeEntry.description,
          billable: this.timeEntry.billable,
          project_id: this.timeEntry.projectId,
          customer_id: this.timeEntry.customerId,
          started_at: this.timeEntry.startedAt,
          stopped_at: this.timeEntry.stoppedAt
        }
      }
    },

    methods: {
      ...mapActions(['createTimeEntry', 'updateTimeEntry', 'deleteTimeEntry']),

      handleSubmitActiveTimeEntry () {
        if (!this.isRunning) {
          return this.handleStartTimeEntry()
        }

        this.updateActiveTimeEntry(this.mappedTimeEntry)
      },

      handleSelectRecentTimeEntry (timeEntry) {
        this.timeEntry = Object.assign({}, timeEntry)

        this.handleSubmitActiveTimeEntry()
      },

      handleStartTimeEntry () {
        this.loading = true

        this.createTimeEntry(Object.assign(this.mappedTimeEntry, {
          started_at: date.formatDate(currentUtcDateTime(), 'YYYY-MM-DD HH:mm:ss')
        }))
          .then(timeEntry => {
            this.timeEntry.startedAt = timeEntry.started_at
          })
          .catch(createErrorFromResponse)
          .then(this.stopLoading)
      },

      handleStopActiveTimeEntry () {
        this.loading = true

        this.updateActiveTimeEntry({
          stopped_at: date.formatDate(currentUtcDateTime(), 'YYYY-MM-DD HH:mm:ss')
        })
          .then(this.resetTimeEntry)
          .then(this.stopLoading)
      },

      handleDeleteActiveTimeEntry () {
        this.loading = true

        this.deleteTimeEntry(this.activeTimeEntry)
          .catch(createErrorFromResponse)
          .then(this.resetTimeEntry)
          .then(this.stopLoading)
      },

      handleActiveTimeEntryUpdated (attribute) {
        if (!this.isRunning) {
          return
        }

        const data = {
          [changeCase.snakeCase(attribute)]: this.timeEntry[attribute]
        }

        this.updateActiveTimeEntry(data)
      },

      handleUpdateStartStoppedTime (data) {
        if (!data['stopped_at']) {
          delete data['stopped_at']
        }

        this.updateActiveTimeEntry(data)
      },

      handleSelectCustomer (customer) {
        this.timeEntry.customerId = customer ? customer.id : customer
        this.timeEntry.customer = customer

        this.handleActiveTimeEntryUpdated('customerId')
      },

      handleSelectProject (project) {
        this.timeEntry.projectId = project ? project.id : null
        this.timeEntry.project = project

        if (project) {
          // If project is fixed price or non billable set time entry billable status to non billable
          this.timeEntry.billable = project.project_type ? ![projectTypes.NON_BILLABLE].includes(project.project_type) : this.timeEntry.billable

          if (project.customer_id && project.customer) {
            this.timeEntry.customerId = project.customer_id
            this.timeEntry.customer = project.customer.data
          }
        }

        this.handleActiveTimeEntryUpdated('projectId')
      },

      updateActiveTimeEntry (data) {
        return this.updateTimeEntry({
          timeEntryId: this.activeTimeEntry.id,
          timeEntry: data
        })
          .catch(createErrorFromResponse)
      },

      resetTimeEntry () {
        this.timeEntry = initialData()
      },

      stopLoading () {
        this.loading = false
      }
    },

    watch: {
      activeTimeEntryDescription (newValue) {
        if (this.timeEntry.description !== newValue) {
          this.timeEntry.description = newValue
        }
      }
    },

    created () {
      if (this.activeTimeEntry) {
        this.timeEntry = {
          description: this.activeTimeEntry.description,
          billable: this.activeTimeEntry.billable,
          customerId: this.activeTimeEntry.customer_id,
          projectId: this.activeTimeEntry.project_id,
          startedAt: this.activeTimeEntry.started_at,
          stoppedAt: this.activeTimeEntry.stopped_at
        }
      }

      this.interval = setInterval(() => {
        this.now = new Date()
      }, 1000)
    },

    beforeDestroy () {
      clearInterval(this.interval)

      this.interval = null
    },

    components: {
      CustomerIndicator,
      CustomerPopover,
      InlineEditDuration,
      ProjectIndicator,
      ProjectPopover,
      RecentTimeEntryInput
    },

    mixins: [
      debounceMixin
    ]
  }
</script>

<style lang="stylus">
  @import '~variables'

  .time-tracking
    &__delete-button
      padding-left: 16px !important
</style>
